// src/features/websites/components/HeadlinesUrlField.tsx
import React from 'react';
import {
  Box,
  TextField,
  Typography,
  Link,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

interface HeadlinesUrlFieldProps {
  headlinesUrl: string;
  websiteUrl: string;
  error?: string;
  isLoading: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const HeadlinesUrlField: React.FC<HeadlinesUrlFieldProps> = ({
  headlinesUrl,
  websiteUrl,
  error,
  isLoading,
  onChange,
}) => {
  // Generate a placeholder based on the website's main URL
  const getPlaceholder = () => {
    if (!websiteUrl) return 'https://example.com/latest-news';
    try {
      const url = new URL(websiteUrl.startsWith('http') ? websiteUrl : `https://${websiteUrl}`);
      return `${url.origin}/news` || `${url.origin}/latest`;
    } catch (e) {
      return 'https://example.com/latest-news';
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" gutterBottom>
        Headlines URL
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Enter the URL of the page where the website publishes its latest headlines or article listings.
        This is typically the home page, news section, or a dedicated "latest articles" page.
      </Typography>
      
      <TextField
        fullWidth
        id="headlinesUrl"
        name="headlinesUrl"
        label="Headlines URL"
        variant="outlined"
        value={headlinesUrl}
        onChange={onChange}
        error={!!error}
        helperText={error || "The page where article headlines and summaries are listed"}
        disabled={isLoading}
        placeholder={getPlaceholder()}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title="This is where the system will look for new article headlines before scraping the full content of each article">
                <InfoIcon color="action" fontSize="small" />
              </Tooltip>
            </InputAdornment>
          ),
        }}
      />
      
      {websiteUrl && !headlinesUrl && (
        <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
          Tip: This is often the home page or news section of the website.
          {' '}
          <Link 
            href={websiteUrl.startsWith('http') ? websiteUrl : `https://${websiteUrl}`} 
            target="_blank" 
            rel="noopener noreferrer"
          >
            Visit site
          </Link>
        </Typography>
      )}
    </Box>
  );
};

export default HeadlinesUrlField;